import type { NormValue } from "../../../helpers/extractTrade";

export type NormEntry = { raw: string; value: NormValue };

// theme colors are CSS vars, so color-mix (not /opacity) derives the wash and
// stays theme-aware; full class literals so Tailwind can scan them
export const BULL_TINT = "bg-[color-mix(in_srgb,var(--ta-green-main)_6%,transparent)]";
export const BEAR_TINT = "bg-[color-mix(in_srgb,var(--ta-pastelPink-main)_6%,transparent)]";

export type ChipTone = "success" | "error" | "neutral";

// `!` important so these beat MUI Chip's own filled styles
export const chipToneClass = (t: ChipTone): string =>
  t === "success"
    ? "!bg-[color-mix(in_srgb,var(--ta-green-main)_15%,transparent)] !text-ta-green-main"
    : t === "error"
      ? "!bg-[color-mix(in_srgb,var(--error-main)_15%,transparent)] !text-error-main"
      : "!bg-mui-black-08 !text-token-text-secondary dark:!bg-mui-white-12";

// shared chip/label-small typography for every header chip
export const CHIP_LABEL = "!text-[11px] !font-normal !leading-[1.38] !tracking-[0.16px] uppercase";

export type TextTone = "primary" | "secondary" | "success" | "warning" | "error";

export const textToneClass = (t: TextTone): string =>
  ({
    primary: "text-token-text-primary",
    secondary: "text-token-text-secondary",
    success: "text-success-main",
    warning: "text-warning-main",
    error: "text-error-main",
  }[t]);

export type StatTone = "warn" | "good" | "bad" | "plain";

export const statToneClass = (t: StatTone): string =>
  t === "warn"
    ? "text-warning-main"
    : t === "good"
      ? "text-success-main"
      : t === "bad"
        ? "text-error-main"
        : "text-token-text-primary";

// matches normalize()'s cleaning so a value can be compared against what was captured
const cleanValue = (s: string): string =>
  s.replace(/\*\*/g, "").replace(/^[\s*–\-:\t]+|[\s*–\-:\t]+$/g, "");

/** Bullet lines in the source not already shown as a field (for "Additional information"). */
export const extraInfoRows = (
  raw: string | undefined,
  normalized: Record<string, NormEntry>,
): { label: string; value: string }[] => {
  if (!raw) return [];
  const shown = new Set(Object.values(normalized).map(e => e.raw));
  const rows: { label: string; value: string }[] = [];
  for (const line of raw.split("\n")) {
    const m = /^[ \t]*[-*+][ \t]+(.+?):[ \t]*(.+\S)[ \t]*$/.exec(line);
    if (!m) continue;
    const label = cleanValue(m[1]);
    const value = cleanValue(m[2]);
    if (!label || !value || shown.has(value)) continue;
    rows.push({ label, value });
  }
  return rows;
};

/** All numbers in a normalized value ("lo–hi" range -> [lo, hi]). */
export const toNums = (v: NormValue | undefined): number[] => {
  if (typeof v === "number") return [v];
  if (typeof v === "string") {
    return v
      .split(/[–-]/)
      .map(x => parseFloat(x.replace(/[^0-9.]/g, "")))
      .filter(n => !Number.isNaN(n));
  }
  return [];
};
export const lowOf = (v: NormValue | undefined): number | null => toNums(v)[0] ?? null;
export const highOf = (v: NormValue | undefined): number | null => {
  const n = toNums(v);
  return n.length ? n[n.length - 1] : null;
};
/** Single numeric value, or null for ranges/strings (spread legs use exact strikes). */
export const num = (v: NormValue | undefined): number | null => (typeof v === "number" ? v : null);

export const fmtMoney = (v: number, decimals = 2, approx = false): string =>
  (approx ? "~" : "")
  + v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const isISO = (v: NormValue | undefined): v is string =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);

/** ISO "YYYY-MM-DD" -> "18 Jun 2026"; null when the value isn't an ISO date. */
export const fmtExpiryISO = (v: NormValue | undefined): string | null => {
  if (!isISO(v)) return null;
  const [y, m, d] = v.split("-");
  return `${+d} ${MONTHS[+m - 1]} ${y}`;
};

/** ISO "YYYY-MM-DD" -> "Jun 21 '25" (compact header-pill form); null when not ISO. */
export const fmtExpiryShort = (v: NormValue | undefined): string | null => {
  if (!isISO(v)) return null;
  const [y, m, d] = v.split("-");
  return `${MONTHS[+m - 1]} ${+d} '${y.slice(2)}`;
};

/** Field form: formatted ISO date, else the LLM's raw text. */
export const fmtExpiryField = (entry?: NormEntry): string | null => {
  if (!entry) return null;
  return fmtExpiryISO(entry.value) ?? entry.raw;
};

/** Whole weeks (or days) from today until an ISO expiry, e.g. "3w out". */
export const timeToExpiryISO = (v: NormValue | undefined): string | null => {
  if (!isISO(v)) return null;
  const ms = new Date(v).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const days = Math.round(ms / 86_400_000);
  return days >= 7 ? `${Math.round(days / 7)}w out` : `${days}d out`;
};

/** Formatted number/date when it normalized, else the LLM's descriptive text. */
export const displayValue = (entry?: NormEntry): string | null => {
  if (!entry) return null;
  const v = entry.value;
  if (typeof v === "number") return fmtMoney(v, v % 1 === 0 ? 0 : 2);
  const iso = fmtExpiryISO(v);
  if (iso) return iso;
  return entry.raw || null;
};

export const isUnlimited = (raw?: string): boolean => !!raw && /unlimited|infinit|∞|no cap/i.test(raw);
export const isApprox = (raw?: string): boolean => !!raw && /[~≈]|approx|around|about/i.test(raw);
